import { useMemo } from "react";

import {STATUS_COLORS, STATUS_ORDER, STATUS_ORDER_INDEX_TO_COLOR_KEY} from "../../../../utils/constants";

/**
 * Rubric levels 1–4: 1 = lowest/worst, 4 = highest/best.
 * Chart order: high (green) → low (red). So segment index i = level (4 - i).
 */

function getDistFraction(dist, level) {
    if (dist == null) return 0;
    const v = Array.isArray(dist)
        ? (dist[level - 1] ?? dist[level] ?? 0)
        : (dist[level] ?? dist[String(level)] ?? 0);
    return Number(v) || 0;
}

export function statusToDisplay(status) {
    return status.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, str => str.toUpperCase());
}

export function useBarChartData({
                                    byGroupData,
                                    selectedIndicator,
                                    nationalDist,
                                    globalDist,
                                    groupLabelFormat = group => group
                                }) {
    return useMemo(() => {
        if (!selectedIndicator) return { labels: [], datasets: [] };
        const indicator_code = selectedIndicator.indicator_code;

        const safeByGroupData = (byGroupData && typeof byGroupData === "object") ? byGroupData : {};

        let groupKeys = Object.keys(safeByGroupData).filter(
            (key) => key !== "National" && key !== "Global"
        );

        const allNumericLike = groupKeys.length > 0 && groupKeys.every((k) => !Number.isNaN(Number(k)));
        if (allNumericLike) {
            groupKeys = groupKeys.sort((a, b) => Number(a) - Number(b));
        }

        const labels = [
            "National Avg.",
            "Global Avg.",
            ...groupKeys.map(groupLabelFormat)
        ];

        const numBars = labels.length;

        const datasets = STATUS_ORDER.map((status, index) => {
            const colorKey = !status || status === ""
                ? STATUS_ORDER_INDEX_TO_COLOR_KEY[index]
                : status;
            const color = STATUS_COLORS[colorKey];
            // index 0 = high (level 4), index 3 = low (level 1)
            const level = 4 - index;
            const data = [
                (() => {
                    const dist = nationalDist || safeByGroupData?.National?.[indicator_code]?.national;
                    return dist ? Math.round(getDistFraction(dist, level) * 100) : 0;
                })(),
                (() => {
                    const dist = globalDist || safeByGroupData?.Global?.[indicator_code]?.global;
                    return dist ? Math.round(getDistFraction(dist, level) * 100) : 0;
                })(),
                ...groupKeys.map(g => {
                    const dist = safeByGroupData?.[g]?.[indicator_code]?.school;
                    return dist ? Math.round(getDistFraction(dist, level) * 100) : 0;
                })
            ];
            return {
                label: status ? statusToDisplay(status) : statusToDisplay(STATUS_ORDER_INDEX_TO_COLOR_KEY[index]),
                backgroundColor: color,
                hoverBackgroundColor: color,
                borderColor: color,
                hoverBorderColor: color,
                borderWidth: 0,
                hoverBorderWidth: 0,
                data,
                stack: "main"
            };
        });

        // Ensure no bar exceeds 100%:
        for (let i = 0; i < numBars; i++) {
            const total = datasets.reduce((s, d) => s + (d.data[i] || 0), 0);
            if (total > 100) {
        const excess = total - 100;
                let maxIdx = 0;
                for (let j = 1; j < datasets.length; j++) {
                    if ((datasets[j].data[i] || 0) > (datasets[maxIdx].data[i] || 0)) maxIdx = j;
                }
                const current = datasets[maxIdx].data[i] || 0;
                datasets[maxIdx].data[i] = Math.max(0, current - excess);
        }
        }

        return { labels, datasets };
    }, [byGroupData, selectedIndicator, groupLabelFormat, nationalDist, globalDist]);
}